import { useState } from 'react';
import type { Product } from '../../../types/product';

export const DEFAULT_MAX_SELECTION = 5;

interface UseProductMultiSelectionParams {
  products: Product[];
  initialProducts?: Product[];
  maxSelection?: number;
}

export function useProductMultiSelection({
  products,
  initialProducts,
  maxSelection = DEFAULT_MAX_SELECTION,
}: UseProductMultiSelectionParams) {
  const [selectedProductsMap, setSelectedProductsMap] = useState<Map<number, Product>>(() => {
    if (initialProducts && initialProducts.length > 0) {
      return new Map(initialProducts.map((p) => [p.id, p]));
    }
    return new Map();
  });
  const [limitError, setLimitError] = useState<string | null>(null);

  const handleToggleProductSelection = (id: number) => {
    const item = products.find((x) => x.id === id);
    if (!item) {
      return;
    }

    if (!selectedProductsMap.has(id) && selectedProductsMap.size >= maxSelection) {
      setLimitError(`Ви можете вибрати максимум ${maxSelection} продуктів`);
      return;
    }

    setLimitError(null);
    setSelectedProductsMap((prev) => {
      const newMap = new Map(prev);
      if (newMap.has(id)) {
        newMap.delete(id);
      } else {
        newMap.set(id, item);
      }
      return newMap;
    });
  };

  const handleClearLimitError = () => {
    setLimitError(null);
  };

  const handleSelectAll = (allProducts: Product[]) => {
    if (allProducts.length > maxSelection) {
      setLimitError(`Ви можете вибрати максимум ${maxSelection} продуктів`);
    } else {
      setLimitError(null);
    }
    setSelectedProductsMap(new Map(allProducts.slice(0, maxSelection).map((p) => [p.id, p])));
  };

  return {
    data: {
      selectedProductsMap,
      limitError,
    },
    actions: {
      handleToggleProductSelection,
      handleSelectAll,
      handleClearLimitError,
    },
  };
}
